import React, { useCallback } from 'react';
import type { RecommendationsServingConfig } from '@noths/polaris-client-listings-api';
import type {
  ProductCarouselNavigatePayload,
  ProductCarouselProductPayload,
} from '@noths/polaris-design-system-candidate-components';
import { ProductCarousel } from '@noths/polaris-design-system-candidate-components';

import type { RecommendedProductsViewModel } from 'src/components/layouts/hooks/useRecommendedProductsViewModel';
import { useListingsRecommendations } from '../hook/useListingsRecommendations';
import {
  trackRecommendedProductClick,
  trackRecommendedProductsCarouselNavigation,
  trackRecommendedProductsImpression,
} from '../tracking/recommendedProductCarouselEvents';

/**
 * The products come from a client-side listings request rather than the store,
 * so the request inputs and the selected option values its events carry are
 * passed in by useRecommendedProductsViewModel. Nothing here reads the store.
 */
export interface RecommendedProductsCarouselContainerProps extends RecommendedProductsViewModel {
  heading: string;
  readonly servingConfig: RecommendationsServingConfig;
}

export const RecommendedProductsCarouselContainer = ({
  currencyCode,
  heading,
  partnerShortCode,
  productCode,
  selectedOptionValues,
  servingConfig,
  visitorId,
}: RecommendedProductsCarouselContainerProps) => {
  const { productsToTrack, recommendedProducts } = useListingsRecommendations({
    currencyCode,
    partnerShortCode,
    productCode,
    servingConfig,
    visitorId,
  });

  /**
   * One impression event for the whole carousel, listing every product, rather
   * than the per-product `onProductVisible` the carousel also offers. Keeps the
   * GA payload identical to the Ribbons carousel this replaces.
   */
  const onVisible = useCallback(() => {
    if (productsToTrack.length === 0) {
      return;
    }

    trackRecommendedProductsImpression({
      products: productsToTrack,
      selectedOptionValues,
      servingConfig,
    });
  }, [selectedOptionValues, productsToTrack, servingConfig]);

  /* The payload's own `product` is the OOUX shape the card renders, which does
     not carry what tracking needs (the numeric product code, the partner
     shortcode, `freeDomesticDelivery`, `salePercentage`), so the index is used to
     look the service product back up. */
  const onProductClick = useCallback(
    (evt: React.MouseEvent, { index }: ProductCarouselProductPayload) => {
      const clickedProduct = productsToTrack[index];

      if (!clickedProduct) {
        return;
      }

      trackRecommendedProductClick(evt, {
        product: clickedProduct,
        productIndex: index,
        selectedOptionValues,
        servingConfig,
      });
    },
    [selectedOptionValues, productsToTrack, servingConfig],
  );

  /* Translated here rather than in the tracking module, so the GA vocabulary this
     surface reports in is untouched by the change of carousel. */
  const onNavigate = useCallback(
    ({ direction, method }: ProductCarouselNavigatePayload) => {
      trackRecommendedProductsCarouselNavigation({
        servingConfig,
        navigation: {
          type: method === 'swipe' ? 'Scroll' : 'Arrow Click',
          direction: direction === 'next' ? 'Right' : 'Left',
        },
      });
    },
    [servingConfig],
  );

  /* Loading, empty and loaded are all delegated to `products`: the carousel
     renders a skeleton for `null`, nothing for `[]`, and the rail once products
     are available. The section's placeholder reserves the same space before the
     fetch even starts, so the rail never jumps from 0px to full height. */
  return (
    <ProductCarousel
      heading={heading}
      onNavigate={onNavigate}
      onProductClick={onProductClick}
      onVisible={onVisible}
      products={recommendedProducts}
    />
  );
};
